import { EventEmitter } from 'events';
import { shopSettingsService } from './shopSettingsService';

/**
 * AI Feature Flags Configuration
 * Controls which AI features are enabled based on subscription tier
 */
export interface AIFeatureFlags {
  enabled: boolean;  // Master AI switch
  modules: {
    clientScoring: boolean;         // CPI calculation & display
    barberScoring: boolean;         // BPI calculation & display
    noShowPrediction: boolean;      // Risk alerts & warnings
    emergencyBooking: boolean;      // VIP smart scheduling
    dynamicPricing: boolean;        // Demand-based pricing
    fraudDetection: boolean;        // Transaction monitoring
    smartScheduling: boolean;       // Gap suggestions & insights
    inventoryPrediction: boolean;   // Stock alerts & ordering
    dailyReports: boolean;          // Automated manager briefings
    cpiBadges: boolean;             // Client achievement badges
    cpiAdjustment: boolean;         // Admin CPI manual adjustment (admin-only)
    timeAttendance: boolean;        // Time clock & attendance tracking
  };
  subscription: 'free' | 'pro' | 'enterprise';
  lastUpdated: Date;
}

/**
 * Subscription tier feature access control
 */
const SUBSCRIPTION_FEATURES = {
  free: {
    enabled: false,
    modules: {
      clientScoring: false,
      barberScoring: false,
      noShowPrediction: false,
      emergencyBooking: false,
      dynamicPricing: false,
      fraudDetection: false,
      smartScheduling: false,
      inventoryPrediction: false,
      dailyReports: false,
      cpiBadges: false,
      cpiAdjustment: false,
      timeAttendance: false
    }
  },
  pro: {
    enabled: true,
    modules: {
      clientScoring: true,          // ✅ CPI scores
      barberScoring: true,          // ✅ BPI scores
      noShowPrediction: true,       // ✅ Risk alerts
      emergencyBooking: false,      // ❌ Enterprise only
      dynamicPricing: false,        // ❌ Enterprise only
      fraudDetection: false,        // ❌ Enterprise only
      smartScheduling: true,        // ✅ Schedule insights
      inventoryPrediction: false,   // ❌ Enterprise only
      dailyReports: false,          // ❌ Enterprise only
      cpiBadges: true,              // ✅ Achievement badges
      cpiAdjustment: true,          // ✅ CPI manual adjustment enabled for Pro
      timeAttendance: true          // ✅ Time clock & attendance
    }
  },
  enterprise: {
    enabled: true,
    modules: {
      clientScoring: true,          // ✅ All features
      barberScoring: true,          // ✅ All features
      noShowPrediction: true,       // ✅ All features
      emergencyBooking: true,       // ✅ All features
      dynamicPricing: true,         // ✅ All features
      fraudDetection: true,         // ✅ All features
      smartScheduling: true,        // ✅ All features
      inventoryPrediction: true,    // ✅ All features
      dailyReports: true,           // ✅ All features
      cpiBadges: true,              // ✅ All features
      cpiAdjustment: true,          // ✅ All features (admin-only)
      timeAttendance: true          // ✅ Time clock & attendance
    }
  }
};

/**
 * AI Settings Service
 * Manages AI feature toggles, subscription tiers, and Firebase persistence
 */
class AISettingsService extends EventEmitter {
  private static instance: AISettingsService;
  private settings: AIFeatureFlags;
  private readonly STORAGE_KEY = 'slikpos_ai_settings';
  private firebaseInitialized = false;

  private constructor() {
    super();
    this.settings = this.loadFromLocalStorage(); // Quick initial load
    this.initFirebase(); // Then sync with Firebase
  }

  static getInstance(): AISettingsService {
    if (!AISettingsService.instance) {
      AISettingsService.instance = new AISettingsService();
    }
    return AISettingsService.instance;
  }

  /**
   * Initialize Firebase sync
   */
  private async initFirebase(): Promise<void> {
    try {
      const firebaseSettings = await shopSettingsService.getSettingsByType<AIFeatureFlags>('ai');
      if (firebaseSettings) {
        this.settings = this.mergeWithDefaults(firebaseSettings);
        this.firebaseInitialized = true;
        // Update localStorage cache
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.settings));
        this.emit('settings-changed', this.settings);
        console.log('🔄 Loaded AI settings from Firebase');
      } else {
        // No Firebase data yet, save current settings to Firebase
        await this.saveToFirebase();
        this.firebaseInitialized = true;
        console.log('📤 Initialized AI settings in Firebase');
      }
    } catch (error) {
      console.error('Failed to initialize AI settings from Firebase:', error);
      this.firebaseInitialized = true; // Continue with localStorage fallback
    }
  }

  /**
   * Load settings from localStorage (fast initial load)
   */
  private loadFromLocalStorage(): AIFeatureFlags {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AIFeatureFlags;
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      console.error('Failed to load AI settings from localStorage:', error);
    }

    // Default: Pro tier for development (enable AI features)
    return {
      enabled: true,
      modules: { ...SUBSCRIPTION_FEATURES.pro.modules },
      subscription: 'pro',
      lastUpdated: new Date()
    };
  }

  /**
   * Merge settings with tier defaults to handle new features
   */
  private mergeWithDefaults(parsed: Partial<AIFeatureFlags>): AIFeatureFlags {
    const subscription = parsed.subscription || 'pro';
    const tierDefaults = SUBSCRIPTION_FEATURES[subscription].modules;

    const mergedModules: AIFeatureFlags['modules'] = {
      clientScoring: parsed.modules?.clientScoring ?? tierDefaults.clientScoring,
      barberScoring: parsed.modules?.barberScoring ?? tierDefaults.barberScoring,
      noShowPrediction: parsed.modules?.noShowPrediction ?? tierDefaults.noShowPrediction,
      emergencyBooking: parsed.modules?.emergencyBooking ?? tierDefaults.emergencyBooking,
      dynamicPricing: parsed.modules?.dynamicPricing ?? tierDefaults.dynamicPricing,
      fraudDetection: parsed.modules?.fraudDetection ?? tierDefaults.fraudDetection,
      smartScheduling: parsed.modules?.smartScheduling ?? tierDefaults.smartScheduling,
      inventoryPrediction: parsed.modules?.inventoryPrediction ?? tierDefaults.inventoryPrediction,
      dailyReports: parsed.modules?.dailyReports ?? tierDefaults.dailyReports,
      cpiBadges: (parsed.modules as any)?.cpiBadges ?? tierDefaults.cpiBadges,
      cpiAdjustment: (parsed.modules as any)?.cpiAdjustment ?? tierDefaults.cpiAdjustment,
      timeAttendance: (parsed.modules as any)?.timeAttendance ?? tierDefaults.timeAttendance
    };

    return {
      enabled: parsed.enabled ?? SUBSCRIPTION_FEATURES[subscription].enabled,
      modules: mergedModules,
      subscription,
      lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : new Date()
    };
  }

  /**
   * Save settings to Firebase and localStorage
   */
  private saveSettings(): void {
    try {
      // Save to localStorage for quick access
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.settings));
      // Save to Firebase
      this.saveToFirebase();
      this.emit('settings-changed', this.settings);
    } catch (error) {
      console.error('Failed to save AI settings:', error);
    }
  }

  /**
   * Save settings to Firebase
   */
  private async saveToFirebase(): Promise<void> {
    try {
      await shopSettingsService.saveSettingsByType('ai', this.settings);
    } catch (error) {
      console.error('Failed to save AI settings to Firebase:', error);
    }
  }

  /**
   * Get current AI settings
   */
  getSettings(): AIFeatureFlags {
    return { ...this.settings };
  }

  /**
   * Check if AI is enabled (master switch)
   */
  isAIEnabled(): boolean {
    return this.settings.enabled;
  }

  /**
   * Check if specific AI feature is enabled
   */
  isFeatureEnabled(featureName: keyof AIFeatureFlags['modules']): boolean {
    if (!this.settings.enabled) return false;
    return this.settings.modules[featureName];
  }

  /**
   * Toggle master AI switch
   */
  toggleAI(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.settings.lastUpdated = new Date();
    this.saveSettings();
  }

  /**
   * Toggle individual AI feature
   * Enforces subscription tier permissions
   */
  toggleFeature(featureName: keyof AIFeatureFlags['modules'], enabled: boolean): boolean {
    // Check if subscription tier allows this feature
    const tierFeatures = SUBSCRIPTION_FEATURES[this.settings.subscription].modules;

    if (enabled && !tierFeatures[featureName]) {
      console.warn(`Feature ${featureName} not available in ${this.settings.subscription} tier`);
      this.emit('feature-restricted', {
        feature: featureName,
        currentTier: this.settings.subscription,
        requiredTier: this.getRequiredTier(featureName)
      });
      return false;
    }

    this.settings.modules[featureName] = enabled;
    this.settings.lastUpdated = new Date();
    this.saveSettings();
    return true;
  }

  /**
   * Update subscription tier
   * Automatically enables/disables features based on new tier
   */
  updateSubscription(tier: 'free' | 'pro' | 'enterprise'): void {
    this.settings.subscription = tier;

    // Update enabled status based on tier
    this.settings.enabled = SUBSCRIPTION_FEATURES[tier].enabled;

    // Apply tier-specific feature permissions
    const tierFeatures = SUBSCRIPTION_FEATURES[tier].modules;
    Object.keys(this.settings.modules).forEach(feature => {
      const featureKey = feature as keyof AIFeatureFlags['modules'];
      this.settings.modules[featureKey] = tierFeatures[featureKey];
    });

    this.settings.lastUpdated = new Date();
    this.saveSettings();

    this.emit('subscription-changed', {
      newTier: tier,
      features: this.settings.modules
    });
  }

  /**
   * Get required subscription tier for a feature
   */
  private getRequiredTier(featureName: keyof AIFeatureFlags['modules']): string {
    if (SUBSCRIPTION_FEATURES.pro.modules[featureName]) return 'pro';
    if (SUBSCRIPTION_FEATURES.enterprise.modules[featureName]) return 'enterprise';
    return 'free';
  }

  /**
   * Get list of features available in a subscription tier
   */
  getFeaturesForTier(tier: 'free' | 'pro' | 'enterprise'): string[] {
    const tierModules = SUBSCRIPTION_FEATURES[tier].modules;
    return Object.entries(tierModules)
      .filter(([_, enabled]) => enabled)
      .map(([feature]) => feature);
  }

  /**
   * Check if upgrade is needed for a feature
   */
  needsUpgrade(featureName: keyof AIFeatureFlags['modules']): {
    needed: boolean;
    currentTier: string;
    requiredTier: string;
  } {
    const currentAllows = SUBSCRIPTION_FEATURES[this.settings.subscription].modules[featureName];
    const requiredTier = this.getRequiredTier(featureName);

    return {
      needed: !currentAllows,
      currentTier: this.settings.subscription,
      requiredTier
    };
  }

  /**
   * Reset all settings to defaults
   */
  resetToDefaults(): void {
    this.settings = {
      enabled: false,
      modules: { ...SUBSCRIPTION_FEATURES.free.modules },
      subscription: 'free',
      lastUpdated: new Date()
    };
    this.saveSettings();
  }

  /**
   * Export settings for backup
   */
  exportSettings(): string {
    return JSON.stringify(this.settings, null, 2);
  }

  /**
   * Import settings from backup
   */
  importSettings(settingsJSON: string): boolean {
    try {
      const imported = JSON.parse(settingsJSON);
      imported.lastUpdated = new Date(imported.lastUpdated);

      // Validate structure
      if (!imported.subscription || !imported.modules) {
        throw new Error('Invalid settings format');
      }

      this.settings = imported;
      this.saveSettings();
      return true;
    } catch (error) {
      console.error('Failed to import settings:', error);
      return false;
    }
  }
}

export const aiSettings = AISettingsService.getInstance();
export default aiSettings;
